<script setup lang="ts">
// Kombo top bar — round-3 (2026-08-31): extracted from NavHeader.vue, bigger (h-6 icon) and
// with more vertical padding than the original inline markup, reused wherever the logo needs to
// show instead of duplicating the `<img>` markup (NavHeader.vue on every screen except
// Live/New match, which hide chrome entirely; LiveMatch.vue directly, since that screen hides
// NavHeader).
//
// Round-5 (2026-08-31) — generalized into a reusable TOP BAR: optional `#left`/`#right` slots
// hold icon buttons (e.g. LiveMatch.vue's Abandon "X", or a future back arrow on some other
// screen) either side of the still-centered logo. Slots over bare icon+handler props: a slot
// lets the CALLER own the button's exact markup/aria-label/disabled-state instead of this
// component guessing a shape that fits every future case.
//
// Round-7 (2026-08-31 item 20) — added an always-on CLOCK (round-12 moved it to the LEFT
// gutter, white/bold/larger — it crowded the right-side actions), so it shows on every screen
// "for free" through this one shared
// component (not just Live match) — e.g. Live match now reads [clock][X] in that order, per the
// coordinator's suggested placement. Renders Europe/Madrid time EXPLICITLY via Intl `timeZone`:
// the kiosk device's own system clock may be running UTC (or anything else) — see DESIGN.md
// section 2/10.2 — so a plain `new Date().toLocaleTimeString()` (device-local tz) would be
// wrong; this always resolves to Madrid wall-clock time regardless of the device's own tz.
// HH:MM granularity, ticks every 15s (no need for anything finer, less churn than 1s), styled
// like the Live match format footer (small, muted, monospace) — discreet by design.
//
// The right gutter dropped its round-5 fixed width (`w-11`) since its content is now variable
// (clock alone vs. clock+icon) — it just hugs its content (`shrink-0`). The left gutter keeps
// its fixed `w-11` (reserved for a future back-arrow icon). This means the logo is centered
// within the space BETWEEN the two gutters, not always perfectly bar-centered when their widths
// differ — round-9 (2026-08-31) fixed that drift by absolutely centering the logo in the bar
// (pointer-events-none, so it never blocks gutter taps).
//
// Round-7 feedback item 23 — a Settings gear sits between the clock and any `#right` slot
// content, so every screen reads [clock][gear][optional slot content] (Live match:
// [clock][gear][Abandon X]). Same reasoning as the clock: Settings needs to be reachable from
// EVERY screen, so it lives in this shared component rather than each view wiring its own.
//
// Round-19 (2026-08-31) — a mic status/toggle button (MicButton.vue) sits just before the gear:
// [clock][mic][gear][optional slot content]. Same "every screen" reasoning again, and it keeps
// the mic on/off + live-detection test one tap away from wherever the kiosk currently is, not
// just Home.
import { onMounted, onUnmounted, ref } from 'vue'
import { mdiCog } from '@mdi/js'
import komboLogo from '../assets/kombo-logo.svg'
import MicButton from './MicButton.vue'
import SettingsPanel from './SettingsPanel.vue'

withDefaults(defineProps<{ bordered?: boolean }>(), { bordered: true })

const settingsOpen = ref(false)

const MADRID_CLOCK_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Madrid',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const clock = ref(MADRID_CLOCK_FORMAT.format(new Date()))
let clockTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  clockTimer = setInterval(() => {
    clock.value = MADRID_CLOCK_FORMAT.format(new Date())
  }, 15000)
})

onUnmounted(() => {
  if (clockTimer) clearInterval(clockTimer)
})
</script>

<template>
  <div class="h-12 shrink-0 relative flex items-center px-2" :class="bordered ? 'border-b border-neutral-800/60' : ''">
    <!-- Round-12: the clock lives in the LEFT gutter (it crowded the gear/actions on the
         right), white + bold + slightly larger per feedback. -->
    <div class="shrink-0 flex items-center justify-start gap-1.5 pl-1">
      <span class="text-[13px] font-bold font-mono text-neutral-100 tabular-nums" aria-label="Current time, Madrid">{{ clock }}</span>
      <slot name="left" />
    </div>
    <!-- Round-9 (2026-08-31): logo is absolutely centered in the BAR, immune to the gutters'
         differing widths (the round-7 hug-content right gutter had it drifting off-center on
         Live match, where the right side holds clock+gear+X against an empty left). -->
    <img :src="komboLogo" alt="Kombo" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-auto opacity-90 pointer-events-none" />
    <div class="flex-1"></div>
    <div class="shrink-0 flex items-center justify-end gap-1.5">
      <MicButton />
      <button
        type="button"
        class="tap-target w-9 h-9 min-h-0 min-w-0 shrink-0 rounded-full active:bg-neutral-800 flex items-center justify-center text-neutral-500"
        aria-label="Settings"
        @click="settingsOpen = true"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor"><path :d="mdiCog" /></svg>
      </button>
      <slot name="right" />
    </div>
  </div>

  <SettingsPanel v-if="settingsOpen" @close="settingsOpen = false" />
</template>
